import * as React from "react";
import {useEffect} from "react";
import {
    DpasPopup,
    IDirectoryContent,
    IFileDetails,
    IMasonryContent,
} from "../Interfaces/IFiles";
import {useDpasData} from "../dpasDataContext";
import {
    AssetURL, DpasFileType, internalNameToDisplayNameType, IsImage, ItemType
} from "../constants";
import {getDirContentNames, getFileAttributesSingle,} from "../BackendFacade/apiCalls";
import {deepCopy, simplifyForHtmlId} from "../../../Utils/transformer";
import Masonry from "react-responsive-masonry";
import {useSearchParams} from "react-router-dom";
import {useMain} from "../../../Utils/SessionControls/mainContext";
import FilesUpload from "./Modals/FilesUpload";
import FilesDetail from "./Modals/FilesDetail";
import OdxCard from "../../../OdxComponents/NativeOdx/Odx-Card";
import OdxIcon from "../../../OdxComponents/NativeOdx/Odx-Icon";
import {odxIconSets} from "../../../OdxComponents/constants";
import OdxAreaHeader from "../../../OdxComponents/NativeOdx/Odx-AreaHeader";
import OdxMainFilterGroup from "../../../OdxComponents/NativeOdx/Odx-MainFilterGroup";
import OdxSearchbar from "../../../OdxComponents/NativeOdx/Odx-Searchbar";
import {downloadListObjectAsCSV} from "../../../Utils/objectDownload";
import {OdxTableRow} from "../../../OdxComponents/NativeOdx/Table/Odx-TableRow";
import {OdxTableCell} from "../../../OdxComponents/NativeOdx/Table/Odx-Tablecell";
import OdxTable from "../../../OdxComponents/NativeOdx/Table/Odx-Table";
import OdxBreadcrumbs from "../../../OdxComponents/NativeOdx/Odx-Breadcrumbs";
import {isUserroleSufficient} from "../../../Utils/authorization";
import {UserRoles} from "../../../Utils";
import {HubPathRouting} from "../../../HubFramework/pathBuilder";
import {orderBy} from "../../../Utils/sorting";

// TODO: upload-valid image types: jpg, jpeg, png, gif, bmp, tif, tiff, webp
type FileList = Record<string, IFileDetails>;

/**
 * Management of directories: Since there are two types of directory types, the URL splits up into different behaviors.
 * The URL to be read is everything after the ?. It is structured as follows:
 * &[projectName]/[directoryType]/[directories]
 * The directoryType is not transmitted to the backend in the URL but as type. It is set by state.directoryType
 */
const Files: React.FC = () => {
    const [popup, setPopup] = React.useState<DpasPopup>(DpasPopup.None);
    const [folderPath, setFolderPath] = React.useState<string[]>([]);
    const [currentDirectory, setCurrentDirectory] = React.useState<IDirectoryContent | undefined>(undefined);
    const [toolbar_SearchField, setToolbar_SearchField] = React.useState<string>("");
    const [directoryType, setDirectoryType] = React.useState<DpasFileType>("");
    const [fileDetails, setFileDetails] = React.useState<IFileDetails | null>(null);
    const [projectId, setProjectId] = React.useState<string>("");
    const [viewDetailsSwitch, setViewDetailsSwitch] = React.useState<boolean>(false);
    const [fileListDetails, setFileListDetails] = React.useState<FileList>({});
    const baseId = 'dpas-files';

    const {getProjectId} = useDpasData();
    const [path, setPath] = useSearchParams();

    const {setLoading, createErrorToast} = useMain();

    useEffect(() => {
        setLoading(true);

        if (path === undefined || path.get("path") === null) {
            setUrlPath("", "");
        }

        let directoryType = path.get("type") as DpasFileType;
        if (!directoryType) {
            directoryType = "";
        }
        setDirectoryType(directoryType);

        let _folderPath = getFolderPathFromString(path.get("path") ?? "");

        // what?? i presume this resets the folder if the type is gone? because you cant be in a folder when you have no type!
        if (_folderPath.length > 1 && directoryType === "") {
            _folderPath = [""];
        }

        setFolderPath(_folderPath);
        openDirectory(_folderPath.join("/"), directoryType);

        getProjectId().then((data) => {
            setProjectId(data);
            setLoading(false);
        }).catch(() => {
            setLoading(false);
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // If detail view is activated, get all details for every file in this folder
    useEffect(() => {
        if (!viewDetailsSwitch) return;

        setLoading(true);
        const dir = getFilteredCurrentDirectory();
        let fileNames = dir.files;
        let newFileListDetails: FileList = {...fileListDetails};

        // Filter all files which are already in fileListDetails
        fileNames = fileNames.filter((name) => {
            return fileListDetails[name] === undefined;
        });

        let promises = fileNames.map((name) => {
            return getFileAttributesSingle(name, directoryType).then((data) => {
                if (data.success) {
                    newFileListDetails[name] = data.data;
                } else {
                    createErrorToast("Error", data.message);
                }
            });
        });

        Promise.all(promises).then(() => {
            setFileListDetails(newFileListDetails);
            setLoading(false);
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [viewDetailsSwitch, currentDirectory]);

    const setUrlPath = (path: string, directoryType: string) => {
        setPath({path: path, type: directoryType});
    };

    const getFolderPathFromString = (path: string): string[] => {
        let _folderPath = path.split("/");
        // The first element must be an empty string
        if (_folderPath.length > 0 && _folderPath[0] !== "") {
            // If there is an empty element because the path ends with a /, remove it
            if (_folderPath.length > 1) {
                if (_folderPath[_folderPath.length - 1] === "") {
                    _folderPath = _folderPath.slice(0, -1);
                }
            }
            _folderPath = [""].concat(_folderPath);
        } else if (_folderPath.length === 0) {
            _folderPath = [""];
        }
        return _folderPath;
    };

    const goUpInFolder = () => {
        const isTopLevelDirectory = folderPath.join("").trim().length == 0;

        if (isTopLevelDirectory) {
            openDirectory("", "");
            return;
        }

        let _folderPath = folderPath.slice(0, -1);
        openDirectory(_folderPath.join("/"), directoryType);
    }

    /**
     * Opens a directory. If no directory is given, the root directory is opened.
     * @param directory - Takes full path as string, without folder type
     * @param type - Takes the folder type
     */
    const openDirectory = (directory: string = "", type: DpasFileType) => {
        // The directory path must not start with / but end with / if it is not empty
        if (directory !== "" && directory[0] === "/") {
            directory = directory.slice(1);
        }
        if (directory !== "" && directory[directory.length - 1] !== "/") {
            directory = directory + "/";
        }

        setDirectoryType(type);
        // On root level: display folders "image" and "documents" first
        if (type === "" && directory === "") {
            setCurrentDirectory({
                directories: [],
                files: [],
                category: Object.keys(internalNameToDisplayNameType),
            });
            setDirectoryType("");
            setUrlPath("", "");
            return;
        }

        const newpath = getFolderPathFromString(directory);
        setFolderPath(newpath);
        setLoading(true);
        getAllContentNames(directory, type).then((data) => {
            setCurrentDirectory(data);
            setLoading(false);
        })
        setUrlPath(newpath.join("/"), type);
    };

    /**
     * Gets all content names of a directory. Requests the backend until the continuation token is null
     * @param direcory - Full path of the directory, without folder type
     * @param type - Type of the directory, can be "image", "document", "secret" or ""
     * @param token - Continuation token to continue the request
     */
    const getAllContentNames = async (direcory: string, type: DpasFileType, token: string = null): Promise<IDirectoryContent> => {
        const data = await getDirContentNames(direcory, type, token);
        if (!data.success) {
            createErrorToast("Error", data.message);
            return {directories: [], files: [], category: [], token: null};
        }
        const content = data.data as IDirectoryContent;
        if (content.directories.length === 0 && content.files.length === 0) {
            return content;
        }
        // If there is a continuation token, continue to request
        if (content.token) {
            const nextContent = await getAllContentNames(
                direcory,
                type,
                content.token.nextMarker
            );
            // Merge the content with the next content
            content.directories = content.directories.concat(nextContent.directories);
            content.files = content.files.concat(nextContent.files);
        }
        return content;
    }

    const convertToMasonryItems = (files: string[], directories: string[]) => {
        let i = 0;
        let ret: IMasonryContent[] = [];

        directories.forEach((dir) => {
            ret.push({
                id: i++,
                name: dir,
                type: ItemType.Directory,
                src: dir,
                folderType: null
            });
        });

        files.forEach((file) => {
            ret.push({
                id: i++,
                name: file,
                type: ItemType.File,
                src: getUrlForFile(file),
            });
        });

        return ret;
    };

    const getUrlForFile = (file: string) => {
        if (IsImage(directoryType)) return AssetURL + "156px/" + projectId + "/" + file;
        return AssetURL + projectId + "/" + file
    }

    const cleanDirectoryName = (dir: string) => {
        return dir
            .split("/")
            .filter((d) => d !== "")
            .pop();
    };

    /**
     * Cleans the filename from the path and the extension
     * @param file
     * @param showExtension
     */
    const cleanFileName = (file: string, showExtension: boolean = false) => {
        if (showExtension) return file.split("/").pop();
        return file.split("/").pop()?.split(".")[0];
    };

    /**
     * Creates a card for the masonry layout
     * @param data
     * @param index
     */
    const DpasCard = ({data}: { data: IMasonryContent }, index: number) => {
        const itemKey = simplifyForHtmlId(data.name);
        switch (data.type) {
            case ItemType.Directory:
                return (
                    <OdxCard
                        onClick={() => openDirectory(data.src, directoryType)}
                        key={index}
                        id={`${baseId}-card-dir-${itemKey}`}
                    >
                        <span className={"flex"}>
                            <OdxIcon
                                iconSet={odxIconSets.core}
                                size={"lg"}
                                name={"folder"}
                            />
                            <div className={"!pl-2 content-center"}>{cleanDirectoryName(data.name)}</div>
                        </span>
                    </OdxCard>
                );

            case ItemType.File:
                // Type image
                let image = <img
                    className="w-full block rounded-tl-[1rem] rounded-tr-[1rem] aspect-square object-contain"
                    alt="logo"
                    src={data.src}
                />

                let filename = (
                    <span className={"truncate block w-full"}>{cleanFileName(data.name)}</span>
                );
                // Type document
                if (!IsImage(directoryType)) {
                    image = (
                        <OdxIcon
                            iconSet={odxIconSets.core}
                            size={"lg"}
                            name={"file"}
                        />
                    );
                    filename = (
                        <span className={"truncate block w-full"}>
                            {cleanFileName(data.name, true)}
                        </span>
                    );
                }
                return (
                    <OdxCard onClick={() => fileDetailOpen(data.name)} key={index} id={`${baseId}-card-file-${itemKey}`}>
                        <span>
                            {image}
                            {filename}
                        </span>
                    </OdxCard>
                );
        }
    };

    /**
     * Filter and sort the view by the settings of the filter bar
     */
    const filterCurrentView = (files: boolean, directories: boolean): IMasonryContent[] => {
        const dir = getFilteredCurrentDirectory();
        if (dir == null) return [];
        return convertToMasonryItems(files ? dir.files : [], directories ? dir.directories : []);
    };

    const getFilteredCurrentDirectory = () => {
        if (currentDirectory == null) return null;

        // Prepare current Directory
        let filteredCurrentDirectory = deepCopy(currentDirectory);

        // Filter by search field
        if (toolbar_SearchField !== "" && toolbar_SearchField.length >= 3) {
            filteredCurrentDirectory.directories = filteredCurrentDirectory.directories.filter((dir) =>
                dir.toLowerCase().includes(toolbar_SearchField.toLowerCase())
            );
            filteredCurrentDirectory.files = filteredCurrentDirectory.files.filter(
                (file) =>
                    file.toLowerCase().includes(toolbar_SearchField.toLowerCase())
            );
        }

        filteredCurrentDirectory.files = orderBy(filteredCurrentDirectory.files, x => x.split("/").pop());

        return filteredCurrentDirectory;
    }

    // Virtual scrolling masonry component
    const VirtualMasonry = ({items}: {items: IMasonryContent[]}) => {
        const [containerWidth, setContainerWidth] = React.useState(window.innerWidth - 100);
        
        // Calculate responsive columns (5-8 columns based on screen width)
        const getColumnsCount = React.useCallback(() => {
            if (containerWidth >= 2000) return 8;
            if (containerWidth >= 1600) return 7;
            if (containerWidth >= 1200) return 6;
            if (containerWidth >= 900) return 5;
            return 4;
        }, [containerWidth]);
        
        React.useEffect(() => {
            const handleResize = () => {
                setContainerWidth(window.innerWidth - 100);
            };
            
            window.addEventListener('resize', handleResize);
            return () => window.removeEventListener('resize', handleResize);
        }, []);

        return <Masonry columnsCount={getColumnsCount()} gutter={"8px"}>
                    {items.map((item, index) => DpasCard({data: item}, index))}
                </Masonry>
    };

    const fileDetailOpen = (file: string) => {
        setLoading(true);
        getFileAttributesSingle(file, directoryType).then((data) => {
            if (data.success) {
                data.data.length = data.data.properties.length;
                setFileDetails(data.data);
                setPopup(DpasPopup.PictureDetail);
            } else {
                createErrorToast("Error", data.message);
            }
            setLoading(false);
        });
    };

    const refreshDirectory = () => {
        openDirectory(folderPath.join("/"), directoryType);
    };

    const uploadFileShow = () => {
        setPopup(DpasPopup.UploadFile);
    };

    const HeaderText = () => {
        let title = folderPath[folderPath.length - 1];
        if (!title) {
            title = internalNameToDisplayNameType[directoryType];
        }

        const dir = getFilteredCurrentDirectory();
        const fileCount = directoryType !== "" ? dir && dir.files.length : 0;

        return (
            <div className={"flex items-center"}>
                {directoryType !== "" && (
                    <odx-icon-button
                        id={`${baseId}-back-btn`}
                        variant="ghost"
                        size="lg"
                        icon={`${odxIconSets.core}::arrow-left`}
                        onClick={() => goUpInFolder()}
                    />
                )}
                <div className={"align-middle inline-block"}>
                    {title}
                    {directoryType !== "" && (
                        <span className="!ml-2 text-gray-500 text-sm">({fileCount} file{fileCount !== 1 ? "s" : ""})</span>
                    )}
                </div>
            </div>
        );
    };

    const downloadFileList = () => {
        const dir = getFilteredCurrentDirectory();
        const files = dir.files.map(x => {
            return {
                name: x.split("/").pop(), // Remove directory name in all names
                src: getUrlForFile(x)
            }
        })

        downloadListObjectAsCSV(files);
    };

    const tableHeaders = [
        {name: "name", title: "Name", sortable: true, required: true},
        {name: "size", title: "File Size", sortable: true, required: true},
        {name: "modified", title: "Last Modified", sortable: true, required: true},
    ];

    const generateTableRows = () => {
        return filterCurrentView(true, true).map((item, index) => {
            const fileName = item.name.split("/").pop() || "";
            const size = fileListDetails[fileName]?.properties.length || 0; // todo: why is "LENGTH" the name for the fileSize!??!?!
            const lastModified = fileListDetails[fileName]?.properties
                .lastModified as string;

            switch (item.type) {
                case ItemType.Directory:
                    return (
                        <OdxTableRow key={index.toString()} onClick={() => openDirectory(item.src, directoryType)} id={`${baseId}-row-dir-${simplifyForHtmlId(item.name)}`}>
                            <OdxTableCell >
                                <div className="flex items-center">
                                    <OdxIcon
                                        iconSet={odxIconSets.core}
                                        size={"md"}
                                        name={"folder"}
                                        className="mr-2"
                                    />
                                    <span>{cleanDirectoryName(item.name)}</span>
                                </div>
                            </OdxTableCell>
                            <OdxTableCell>-</OdxTableCell>
                            <OdxTableCell>-</OdxTableCell>
                        </OdxTableRow>
                    );
                case ItemType.File:
                    return (
                        <OdxTableRow key={index.toString()} onClick={() => fileDetailOpen(item.name)} id={`${baseId}-row-file-${simplifyForHtmlId(item.name)}`}>
                            <OdxTableCell >
                                <div className="flex items-center">
                                    <OdxIcon
                                        iconSet={odxIconSets.core}
                                        size={"md"}
                                        name={"file"}
                                        className="mr-2"
                                    />
                                    <span>{fileName}</span>
                                </div>
                            </OdxTableCell>
                            <OdxTableCell>
                                {item.type === ItemType.File &&
                                    Math.round((size / 1024 / 1024) * 100) / 100}{" "}
                                MB
                            </OdxTableCell>
                            <OdxTableCell>
                                {lastModified
                                    ? lastModified.split("+")[0].replace("T", " ")
                                    : ""}
                            </OdxTableCell>
                        </OdxTableRow>
                    );
            }
        });
    };

    const ListViewTable = () => {
        return (
            <div id={`${baseId}-table-view`}>
                <OdxTable
                    headerData={tableHeaders}
                    variant="default"
                    paginated={true}
                    id={`${baseId}-table`}
                    itemsPerPage={10}
                >
                    {generateTableRows()}
                </OdxTable>
            </div>
        );
    };

    const Gridview = () => {
        if (directoryType === "") {
            return (
                <>
                    <OdxAreaHeader  text={"Folders"} id={`${baseId}-root-folders-header`}/>
                    <Masonry columnsCount={5} gutter={"8px"}>
                        {Object.keys(internalNameToDisplayNameType).map((type, i) => {
                            return (
                                <OdxCard
                                    onClick={() => {
                                        openDirectory("", type as DpasFileType);
                                    }}
                                    id={`${baseId}-root-${simplifyForHtmlId(type)}`}
                                    key={i}
                                >
                        <span className={"flex"}>
                            <OdxIcon
                                iconSet={odxIconSets.core}
                                size={"lg"}
                                name={"folder"}
                            />
                            <div className={"!pl-2 content-center"}>{cleanDirectoryName(type)}</div>
                        </span>
                                </OdxCard>
                            )
                        })
                        }
                    </Masonry>
                </>
            );
        }

        const Folders = filterCurrentView(false, true);
        const Files = filterCurrentView(true, false);
        
        return (
            <>
                {Folders.length > 0 && (
                    <>
                        <OdxAreaHeader text={"Folders"} id={`${baseId}-folders-header`}/>
                        <Masonry columnsCount={5} gutter={"8px"}>
                            {Folders.map((item, index) => DpasCard({data: item}, index))}
                        </Masonry>
                    </>
                )}
                <OdxAreaHeader  text={"Files"} id={`${baseId}-files-header`}/>
                <VirtualMasonry items={Files} />
            </>
        );
    };

    const ContentView = () => {
        if (!viewDetailsSwitch || directoryType === "") {
            return <Gridview/>;
        } else {
            return <ListViewTable/>;
        }
    };

    return (
        <div id={`${baseId}-view`}>
            <OdxBreadcrumbs id={`${baseId}-breadcrumbs`}/>

            <div>
                {popup !== DpasPopup.None && (
                    <>
                        {popup === DpasPopup.PictureDetail && fileDetails && (
                            <FilesDetail
                                fileDetails={fileDetails}
                                directoryType={directoryType}
                                refreshDirectory={refreshDirectory}
                                setPopup={setPopup}
                                projectId={projectId}
                            />
                        )}
                        {popup === DpasPopup.UploadFile && (
                            <FilesUpload
                                directoryType={directoryType}
                                refreshDirectory={refreshDirectory}
                                setPopup={setPopup}
                                uploadPathParam={folderPath.slice(1).join("/") + "/"}
                            />
                        )}
                    </>
                )}
            </div>

            <div className={"h-[calc(100vh-150px)] overflow-x-hidden overflow-y-hidden m-0"} id={`${baseId}-content`}>
                <OdxAreaHeader className={"!pl-0"} text={<HeaderText/>} id={`${baseId}-header`}/>
                <OdxMainFilterGroup>
                    <div className="flex-grow odx-padding-top-16">
                        <OdxSearchbar onSearch={setToolbar_SearchField} id={`${baseId}-search`}/>
                    </div>

                    <div className=" odx-padding-top-16">
                    {isUserroleSufficient(UserRoles.editor, HubPathRouting.currentProject?.id, HubPathRouting.currentModule.id) && (
                        <odx-button
                            id={`${baseId}-upload-btn`}
                            variant="neutral"
                            size="lg"
                            type="button"
                            disabled={directoryType === "" ? true : undefined}
                            onClick={uploadFileShow}
                            className="!mt-0 !mb-0 !mr-8"
                        >
                            <odx-icon slot="prefix" size="md" name={`${odxIconSets.core}::upload`}></odx-icon>
                            Upload file
                        </odx-button>
                    )}
                    <odx-button
                        id={`${baseId}-download-list-btn`}
                        variant="neutral"
                        size="lg"
                        type="button"
                        disabled={directoryType === "" ? true : undefined}
                        onClick={downloadFileList}
                        className="!mt-0 !mb-0 !mr-8"
                    >
                        <odx-icon slot="prefix" size="md" name={`${odxIconSets.core}::download`}></odx-icon>
                        Download file list
                    </odx-button>
                    <odx-icon-button
                        id={`${baseId}-toggle-view-btn`}
                        variant="ghost"
                        size="lg"
                        icon={viewDetailsSwitch ? `${odxIconSets.core}::menu` : `${odxIconSets.core}::dashboard`}
                        disabled={directoryType === "" ? true : undefined}
                        onClick={
                            viewDetailsSwitch
                                ? () => setViewDetailsSwitch(false)
                                : () => setViewDetailsSwitch(true)
                        }
                        className="!mt-0 !mb-0 !mr-4"
                    />
                    </div>
                </OdxMainFilterGroup>

                <div className={"overflow-y-scroll h-[calc(100%-150px)] m-0"} id={`${baseId}-results`}>
                    <ContentView/>
                </div>
            </div>
        </div>
    );
};

export default Files;
